import {
  CSSProperties,
  PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from "react"
import {
  classNames,
  emptyPoint,
  emptySize,
  findFirstRelativeParent,
  Point,
  Rect,
  Size,
} from "utils/helpers"
import {
  DragDropHandler,
  DropTargetData,
  useDragDrop,
} from "./drag-drop-context"
import "./drag-target.less"

interface DragProps {
  dragTargetId: string
  className?: string
  draggable?: boolean
  onDragStart?: DragDropHandler
  onDragEnd?: DragDropHandler
  role?: string
  style?: CSSProperties
}

const MOUSE_TOUCH_ID = -1

/**
 * Bookkeeping of one drag in progress. Kept in a ref, not in React state:
 * mouse and touch events arrive faster than React commits, and a handler
 * that reads a stale closure loses moves or ends the wrong drag.
 */
interface ActiveDrag {
  touchId: number
  /** item position relative to its positioned parent (what is rendered) */
  relPos: Point
  startingRelPos: Point
  /** item position in page coordinates (what the drop zones are hit-tested with) */
  pagePos: Point
  /** item position minus the finger/pointer position, both spaces */
  relOffset: Point
  pageOffset: Point
  size: Size
  overZoneId: string | null
  startingZoneId: string | null
}

/**
 * A draggable item of the drag-and-drop screen. Works with touch (the
 * element's own listeners, registered non-passive so the page never scrolls
 * under a drag) and with the mouse (window listeners registered once).
 */
export function DragTarget(props: PropsWithChildren<DragProps>) {
  const { draggable, onDragStart, onDragEnd, children, dragTargetId, role } =
    props
  const element = useRef<HTMLDivElement>(null)
  const dragDrop = useDragDrop()

  // what the component renders
  const [render, setRender] = useState({
    isDragging: false,
    isDropped: false,
    relPos: emptyPoint(),
    size: emptySize(),
  })
  // the latest props/context for the long-lived event handlers
  const latest = useRef({ dragDrop, onDragStart, onDragEnd, draggable, dragTargetId })
  latest.current = { dragDrop, onDragStart, onDragEnd, draggable, dragTargetId }

  const drag = useRef<ActiveDrag | null>(null)

  /** Begins a drag at the pointer position; false when this item cannot start one now. */
  function start(pointer: Point, touchId: number): boolean {
    const { dragDrop, onDragStart, draggable, dragTargetId } = latest.current

    if (!draggable || drag.current || !element.current) return false
    // one drag per finger; the mouse is a finger with a fixed id
    if (dragDrop.usedTouchIds.indexOf(touchId) !== -1) return false

    const container = findFirstRelativeParent(element.current)
    const rt = element.current.getBoundingClientRect()
    const rtContainer = container.getBoundingClientRect()

    const relPos = { x: rt.x - rtContainer.x, y: rt.y - rtContainer.y }
    const pagePos = { x: rt.x, y: rt.y }
    const size = { w: rt.width, h: rt.height }
    const startingZone = findContainingTarget(dragDrop.targets, pagePos, size)

    drag.current = {
      touchId,
      relPos,
      startingRelPos: relPos,
      pagePos,
      relOffset: { x: relPos.x - pointer.x, y: relPos.y - pointer.y },
      pageOffset: { x: pagePos.x - pointer.x, y: pagePos.y - pointer.y },
      size,
      overZoneId: null,
      startingZoneId: startingZone?.id ?? null,
    }

    setRender({ isDragging: true, isDropped: false, relPos, size })

    // drag-start is delivered before any drag-over, and outside React's event dispatch
    setTimeout(() => {
      dragDrop.addTouchId(touchId)

      if (startingZone) {
        dragDrop.onDragOver({
          itemId: dragTargetId,
          targetId: startingZone.id,
          onUserDragStart: onDragStart,
        })
      } else if (onDragStart) {
        onDragStart(dragTargetId)
      }
    })

    return true
  }

  function move(pointer: Point) {
    const d = drag.current
    if (!d) return

    const { dragDrop, dragTargetId } = latest.current

    d.relPos = { x: pointer.x + d.relOffset.x, y: pointer.y + d.relOffset.y }
    d.pagePos = { x: pointer.x + d.pageOffset.x, y: pointer.y + d.pageOffset.y }

    const over = findContainingTarget(dragDrop.targets, d.pagePos, d.size)
    const overZoneId = over?.id ?? null

    if (overZoneId !== d.overZoneId) {
      const left = d.overZoneId
      d.overZoneId = overZoneId

      if (overZoneId === null) {
        setTimeout(() => dragDrop.onDragExit({ itemId: dragTargetId, targetId: left! }))
      } else {
        setTimeout(() => dragDrop.onDragOver({ itemId: dragTargetId, targetId: overZoneId }))
      }
    }

    const relPos = d.relPos
    setRender((prev) => ({ ...prev, isDragging: true, relPos }))
  }

  function end() {
    const d = drag.current
    if (!d) return

    drag.current = null
    const { dragDrop, onDragEnd, dragTargetId } = latest.current

    setTimeout(() => dragDrop.removeTouchId(d.touchId))

    if (onDragEnd) {
      onDragEnd(dragTargetId)
    }

    const droppedInNewZone = d.overZoneId !== null && d.overZoneId !== d.startingZoneId

    if (droppedInNewZone) {
      const targetId = d.overZoneId!
      setTimeout(() => dragDrop.onDrop({ itemId: dragTargetId, targetId }))
      setRender({ isDragging: false, isDropped: true, relPos: d.relPos, size: d.size })
    } else {
      if (d.overZoneId !== null) {
        // released over the zone it came from: nothing changes there
        const targetId = d.overZoneId
        setTimeout(() => dragDrop.onDragExit({ itemId: dragTargetId, targetId }))
      }

      setRender({ isDragging: false, isDropped: false, relPos: d.startingRelPos, size: d.size })
    }
  }

  // Touch: the element's own listeners, non-passive so preventDefault really
  // cancels the browser's scroll gesture. React registers touch handlers as
  // passive, which is why the JSX props cannot be used here.
  useEffect(() => {
    const el = element.current
    if (!el || !draggable) return

    function pointOf(e: TouchEvent): Point | null {
      const t = e.targetTouches[0] ?? e.changedTouches[0]
      return t ? { x: t.pageX, y: t.pageY } : null
    }

    function onTouchStart(e: TouchEvent) {
      e.preventDefault()
      const t = e.targetTouches[0]
      if (t) start({ x: t.pageX, y: t.pageY }, t.identifier)
    }

    function onTouchMove(e: TouchEvent) {
      e.preventDefault()
      const p = pointOf(e)
      if (!p) return

      // a start refused a moment ago (finger id still marked used) gets another chance
      if (!drag.current) {
        const t = e.targetTouches[0]
        if (t) start(p, t.identifier)
        return
      }

      move(p)
    }

    function onTouchEnd(e: TouchEvent) {
      if (!drag.current) return
      e.preventDefault()
      end()
    }

    const options: AddEventListenerOptions = { passive: false }
    el.addEventListener("touchstart", onTouchStart, options)
    el.addEventListener("touchmove", onTouchMove, options)
    el.addEventListener("touchend", onTouchEnd, options)
    el.addEventListener("touchcancel", onTouchEnd, options)

    return () => {
      el.removeEventListener("touchstart", onTouchStart)
      el.removeEventListener("touchmove", onTouchMove)
      el.removeEventListener("touchend", onTouchEnd)
      el.removeEventListener("touchcancel", onTouchEnd)
    }
  }, [draggable])

  // Mouse: the drag begins on mousedown on the item (see onMouseDown in the JSX),
  // then window listeners follow the pointer and end it. They read the refs, so
  // a re-render of the item mid-drag (the pile re-lays out) cannot lose the drag.
  useEffect(() => {
    if (!draggable) return

    function onMouseMove(e: MouseEvent) {
      if (drag.current) move({ x: e.pageX, y: e.pageY })
    }

    function onMouseUp() {
      if (drag.current) end()
    }

    window.addEventListener("mousemove", onMouseMove)
    window.addEventListener("mouseup", onMouseUp)

    return () => {
      window.removeEventListener("mousemove", onMouseMove)
      window.removeEventListener("mouseup", onMouseUp)
    }
  }, [draggable])

  if (render.isDropped) {
    return null
  }

  const className = classNames("cb-drag-target", props.className, {
    draggable: draggable,
    "is-dragging": render.isDragging,
  })

  const style: CSSProperties = { ...props.style }
  if (render.isDragging) {
    style.position = "absolute"
    style.left = render.relPos.x
    style.top = render.relPos.y
    style.width = render.size.w
    style.height = render.size.h
  }

  if (draggable) {
    return (
      <div
        className={className}
        ref={element}
        draggable={false}
        style={style}
        role={role}
        onMouseDown={(e) => {
          // left button only; the window listeners take it from here
          if (e.button === 0) start({ x: e.pageX, y: e.pageY }, MOUSE_TOUCH_ID)
        }}
      >
        {children}
      </div>
    )
  }

  return (
    <div className={className} ref={element} draggable={false} style={style}>
      {children}
    </div>
  )
}

function findContainingTarget(
  targets: { [id: string]: DropTargetData },
  pagePos: Point,
  size: Size
): DropTargetData | undefined {
  return Object.values(targets).find((target) =>
    targetContainsItem(target, { x: pagePos.x, y: pagePos.y, w: size.w, h: size.h })
  )
}

function contains(rect: DOMRect, x: number, y: number): boolean {
  return (
    rect.left <= x &&
    x < rect.right &&
    rect.top <= y &&
    y < rect.bottom
  )
}

function targetContainsItem(target: DropTargetData, itemRect: Rect) {
  const rect = target.getRect()

  // must be contained at least 50%
  const q = 0.5

  const itemLeft = itemRect.x
  const itemRight = itemRect.x + itemRect.w
  const itemTop = itemRect.y
  const itemBottom = itemRect.y + itemRect.h
  const requiredWidth = q * itemRect.w
  const requiredHeight = q * itemRect.h

  const topLeft =
    contains(rect, itemLeft, itemTop) &&
    contains(rect, itemLeft, itemTop + requiredHeight) &&
    contains(rect, itemLeft + requiredWidth, itemTop)
  const topRight =
    contains(rect, itemRight, itemTop) &&
    contains(rect, itemRight, itemTop + requiredHeight) &&
    contains(rect, itemRight - requiredWidth, itemTop)
  const bottomLeft =
    contains(rect, itemLeft, itemBottom) &&
    contains(rect, itemLeft, itemBottom - requiredHeight) &&
    contains(rect, itemLeft + requiredWidth, itemBottom)
  const bottomRight =
    contains(rect, itemRight, itemBottom) &&
    contains(rect, itemRight, itemBottom - requiredHeight) &&
    contains(rect, itemRight - requiredWidth, itemBottom)

  return topLeft || topRight || bottomLeft || bottomRight
}
